// Per-user persistence for the "Bold, darker text" setting. It is kept in localStorage keyed by the
// signed-in account, so two people sharing a PC each keep their own choice.
import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useMaterialUIController, setBoldText } from "context";

const useBoldTextStorageKey = (): string | null => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  return account?.homeAccountId ? `flow.boldText.${account.homeAccountId}` : null;
};

// Reads the signed-in user's saved setting into the UI context. Mounted once, in MainLayout.
export const useLoadBoldTextSetting = () => {
  const [, dispatch] = useMaterialUIController();
  const key = useBoldTextStorageKey();

  useEffect(() => {
    if (!key) return;
    let saved = false;
    try {
      saved = localStorage.getItem(key) === "true";
    } catch (e) {
      console.warn(`Could not read the bold text setting (${key}); using the default (off).`, e);
    }
    setBoldText(dispatch, saved);

    // Another tab flipped the setting: apply it here too. The storage event only fires in the
    // other tabs, never in the one that saved.
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setBoldText(dispatch, e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, dispatch]);
};

// Current setting and a setter that applies it immediately and saves it for the signed-in user.
export const useBoldTextSetting = (): [boolean, (value: boolean) => void] => {
  const [controller, dispatch] = useMaterialUIController();
  const key = useBoldTextStorageKey();

  const update = (value: boolean) => {
    setBoldText(dispatch, value);
    if (!key) {
      console.warn("No signed-in account; the bold text setting applies to this session only.");
      return;
    }
    try {
      localStorage.setItem(key, String(value));
    } catch (e) {
      console.warn(`Could not save the bold text setting (${key}); it applies to this session only.`, e);
    }
  };

  return [controller.boldText, update];
};
